// Earliest date the audit.llm_usage table has data for. Lets the date pickers
// clamp to a sensible minimum and the page show a hint if the user picks an
// earlier window.
export const DATA_START_DATE = "2026-03-26";

// The /admin/query endpoint passes JS parameters through System.Text.Json, which
// produces JsonElement values that Npgsql binds as jsonb. Postgres won't cast
// jsonb to timestamptz/uuid, so all dashboards in this repo interpolate
// validated values into the SQL string instead. We do the same here.

const ISO_TS_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z$/;
const UUID_RE   = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PG_GRAIN  = new Set(["hour", "day", "week", "month"]);

function safeIsoTimestamp(t: string): string {
  if (!ISO_TS_RE.test(t)) {
    throw new Error(`Invalid ISO timestamp for SQL: ${t}`);
  }
  return t;
}

function safeUuidOrNull(id: string | null): string {
  if (id === null) return "NULL";
  if (!UUID_RE.test(id)) {
    throw new Error(`Invalid UUID for SQL: ${id}`);
  }
  return `'${id}'::uuid`;
}

function safeGrain(grain: string): string {
  if (!PG_GRAIN.has(grain)) {
    throw new Error(`Invalid date_trunc grain: ${grain}`);
  }
  return grain;
}

export type SqlInputs = {
  start: string;
  end: string;
  companyExternalId: string | null;
  // Postgres date_trunc granularity: 'hour' | 'day' | 'week' | 'month'.
  grain: string;
};

// Aggregated by (name, model). Drives KPI tiles, feature overview table,
// spend-by-feature toplist, and requests-per-feature bar.
export function buildOverviewSql({ start, end, companyExternalId }: SqlInputs): string {
  const s = safeIsoTimestamp(start);
  const e = safeIsoTimestamp(end);
  const c = safeUuidOrNull(companyExternalId);
  return `
SELECT
  name,
  model,
  COUNT(*)::bigint                                                   AS calls,
  COALESCE(SUM(estimated_cost_usd), 0)::numeric                      AS total_cost,
  COALESCE(AVG(estimated_cost_usd), 0)::numeric                      AS avg_cost,
  COALESCE(SUM(input_tokens), 0)::bigint                             AS input_tokens,
  COALESCE(SUM(cached_input_tokens), 0)::bigint                      AS cached_input_tokens,
  COALESCE(SUM(output_tokens), 0)::bigint                            AS output_tokens,
  COALESCE(SUM(reasoning_tokens), 0)::bigint                         AS reasoning_tokens,
  COALESCE(
    SUM(input_tokens + cached_input_tokens + output_tokens + reasoning_tokens),
    0
  )::bigint                                                          AS total_tokens,
  COALESCE(
    PERCENTILE_CONT(0.95) WITHIN GROUP (ORDER BY duration_ms)::bigint,
    0
  )::bigint                                                          AS p95_latency_ms
FROM audit.llm_usage
WHERE created_date_utc >= '${s}'::timestamptz
  AND created_date_utc <  '${e}'::timestamptz
  AND (${c} IS NULL OR company_external_id = ${c})
GROUP BY name, model
ORDER BY total_cost DESC NULLS LAST
`;
}

export type OverviewRow = {
  name: string;
  model: string;
  calls: number;
  total_cost: number;
  avg_cost: number;
  input_tokens: number;
  cached_input_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
  total_tokens: number;
  p95_latency_ms: number;
};

// Aggregated by (name, bucket) for the cost-over-time stack and the latency
// p95-by-feature line chart. Limited to the 10 features with the highest
// spend in the window so the chart stays readable. The bucket granularity
// follows the user's selection (hour / day / week / month).
export function buildFeatureDailySql({ start, end, companyExternalId, grain }: SqlInputs): string {
  const s = safeIsoTimestamp(start);
  const e = safeIsoTimestamp(end);
  const c = safeUuidOrNull(companyExternalId);
  const g = safeGrain(grain);
  return `
WITH top_features AS (
  SELECT name
  FROM audit.llm_usage
  WHERE created_date_utc >= '${s}'::timestamptz
    AND created_date_utc <  '${e}'::timestamptz
    AND (${c} IS NULL OR company_external_id = ${c})
  GROUP BY name
  ORDER BY SUM(estimated_cost_usd) DESC NULLS LAST
  LIMIT 10
)
SELECT
  date_trunc('${g}', created_date_utc AT TIME ZONE 'UTC')             AS bucket,
  name,
  COUNT(*)::bigint                                                   AS calls,
  COALESCE(SUM(estimated_cost_usd), 0)::numeric                      AS cost,
  COALESCE(
    PERCENTILE_CONT(0.95) WITHIN GROUP (ORDER BY duration_ms)::bigint,
    0
  )::bigint                                                          AS p95_latency_ms
FROM audit.llm_usage
WHERE created_date_utc >= '${s}'::timestamptz
  AND created_date_utc <  '${e}'::timestamptz
  AND (${c} IS NULL OR company_external_id = ${c})
  AND name IN (SELECT name FROM top_features)
GROUP BY bucket, name
ORDER BY bucket, name
`;
}

export type FeatureDailyRow = {
  bucket: string;
  name: string;
  calls: number;
  cost: number;
  p95_latency_ms: number;
};

// Top 50 companies by spend in the window, joined to public.companies for
// display name. The optional company filter shrinks this to a single row when
// active, which is by design (drill-in mode).
export function buildCompanySql({ start, end, companyExternalId }: SqlInputs): string {
  const s = safeIsoTimestamp(start);
  const e = safeIsoTimestamp(end);
  const c = safeUuidOrNull(companyExternalId);
  return `
SELECT
  u.company_external_id,
  COALESCE(c.name, u.company_external_id::text)                      AS company_name,
  COUNT(*)::bigint                                                   AS calls,
  COALESCE(SUM(u.estimated_cost_usd), 0)::numeric                    AS total_cost,
  COALESCE(AVG(u.estimated_cost_usd), 0)::numeric                    AS avg_cost,
  COALESCE(
    SUM(u.input_tokens + u.cached_input_tokens + u.output_tokens + u.reasoning_tokens),
    0
  )::bigint                                                          AS total_tokens
FROM audit.llm_usage u
LEFT JOIN public.companies c ON c.external_id = u.company_external_id
WHERE u.created_date_utc >= '${s}'::timestamptz
  AND u.created_date_utc <  '${e}'::timestamptz
  AND u.company_external_id IS NOT NULL
  AND (${c} IS NULL OR u.company_external_id = ${c})
GROUP BY u.company_external_id, c.name
ORDER BY total_cost DESC NULLS LAST
LIMIT 50
`;
}

export type CompanyRow = {
  company_external_id: string;
  company_name: string;
  calls: number;
  total_cost: number;
  avg_cost: number;
  total_tokens: number;
};

// Top 50 users by spend, with display name from public.users and the
// associated company name from public.companies.
export function buildUserSql({ start, end, companyExternalId }: SqlInputs): string {
  const s = safeIsoTimestamp(start);
  const e = safeIsoTimestamp(end);
  const c = safeUuidOrNull(companyExternalId);
  return `
SELECT
  u.user_external_id,
  TRIM(BOTH FROM CONCAT_WS(' ', us.first_name, us.last_name))        AS user_name,
  us.email                                                           AS user_email,
  u.company_external_id,
  COALESCE(c.name, u.company_external_id::text)                      AS company_name,
  COUNT(*)::bigint                                                   AS calls,
  COALESCE(SUM(u.estimated_cost_usd), 0)::numeric                    AS total_cost,
  COALESCE(AVG(u.estimated_cost_usd), 0)::numeric                    AS avg_cost,
  COALESCE(
    SUM(u.input_tokens + u.cached_input_tokens + u.output_tokens + u.reasoning_tokens),
    0
  )::bigint                                                          AS total_tokens
FROM audit.llm_usage u
LEFT JOIN public.users     us ON us.external_id = u.user_external_id
LEFT JOIN public.companies c  ON c.external_id  = u.company_external_id
WHERE u.created_date_utc >= '${s}'::timestamptz
  AND u.created_date_utc <  '${e}'::timestamptz
  AND u.user_external_id IS NOT NULL
  AND (${c} IS NULL OR u.company_external_id = ${c})
GROUP BY u.user_external_id, us.first_name, us.last_name, us.email, u.company_external_id, c.name
ORDER BY total_cost DESC NULLS LAST
LIMIT 50
`;
}

export type UserRow = {
  user_external_id: string;
  user_name: string;
  user_email: string;
  company_external_id: string | null;
  company_name: string;
  calls: number;
  total_cost: number;
  avg_cost: number;
  total_tokens: number;
};

// Top 10 companies by spend, daily series. The company-name JOIN is deferred
// until after the aggregation so the planner can use the company_external_id
// index; otherwise the query times out at 7d+. Skipped from the page when a
// single-company drill-in is active.
export function buildCompanyDailySql({ start, end, grain }: { start: string; end: string; grain: string }): string {
  const s = safeIsoTimestamp(start);
  const e = safeIsoTimestamp(end);
  const g = safeGrain(grain);
  return `
WITH top_companies AS (
  SELECT company_external_id
  FROM audit.llm_usage
  WHERE created_date_utc >= '${s}'::timestamptz
    AND created_date_utc <  '${e}'::timestamptz
    AND company_external_id IS NOT NULL
  GROUP BY company_external_id
  ORDER BY SUM(estimated_cost_usd) DESC NULLS LAST
  LIMIT 10
),
bucketed AS (
  SELECT
    date_trunc('${g}', u.created_date_utc AT TIME ZONE 'UTC')        AS bucket,
    u.company_external_id,
    SUM(u.estimated_cost_usd)                                        AS cost
  FROM audit.llm_usage u
  JOIN top_companies t ON t.company_external_id = u.company_external_id
  WHERE u.created_date_utc >= '${s}'::timestamptz
    AND u.created_date_utc <  '${e}'::timestamptz
  GROUP BY bucket, u.company_external_id
)
SELECT
  d.bucket,
  d.company_external_id,
  COALESCE(c.name, d.company_external_id::text)                      AS company_name,
  COALESCE(d.cost, 0)::numeric                                       AS cost
FROM bucketed d
LEFT JOIN public.companies c ON c.external_id = d.company_external_id
ORDER BY d.bucket, d.company_external_id
`;
}

export type CompanyDailyRow = {
  bucket: string;
  company_external_id: string;
  company_name: string;
  cost: number;
};

// Generic mapper. Each map function below uses the same shape but with the
// specific row type. We rebuild the column index once per call to keep
// per-row work minimal even on the larger company-daily result.
function makeIndexer(columns: string[]) {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  return (row: unknown[], k: string): unknown => {
    const i = idx[k];
    return i === undefined ? undefined : row[i];
  };
}

const num = (v: unknown): number => (v == null ? 0 : Number(v));
const str = (v: unknown): string => (v == null ? "" : String(v));
const orNull = (v: unknown): string | null => (v == null ? null : String(v));

export function mapOverviewRows(columns: string[], rows: unknown[][]): OverviewRow[] {
  const get = makeIndexer(columns);
  return rows.map((r) => ({
    name:                str(get(r, "name")),
    model:               str(get(r, "model")),
    calls:               num(get(r, "calls")),
    total_cost:          num(get(r, "total_cost")),
    avg_cost:            num(get(r, "avg_cost")),
    input_tokens:        num(get(r, "input_tokens")),
    cached_input_tokens: num(get(r, "cached_input_tokens")),
    output_tokens:       num(get(r, "output_tokens")),
    reasoning_tokens:    num(get(r, "reasoning_tokens")),
    total_tokens:        num(get(r, "total_tokens")),
    p95_latency_ms:      num(get(r, "p95_latency_ms")),
  }));
}

export function mapFeatureDailyRows(columns: string[], rows: unknown[][]): FeatureDailyRow[] {
  const get = makeIndexer(columns);
  return rows.map((r) => ({
    bucket:         str(get(r, "bucket")),
    name:           str(get(r, "name")),
    calls:          num(get(r, "calls")),
    cost:           num(get(r, "cost")),
    p95_latency_ms: num(get(r, "p95_latency_ms")),
  }));
}

export function mapCompanyRows(columns: string[], rows: unknown[][]): CompanyRow[] {
  const get = makeIndexer(columns);
  return rows.map((r) => ({
    company_external_id: str(get(r, "company_external_id")),
    company_name:        str(get(r, "company_name")),
    calls:               num(get(r, "calls")),
    total_cost:          num(get(r, "total_cost")),
    avg_cost:            num(get(r, "avg_cost")),
    total_tokens:        num(get(r, "total_tokens")),
  }));
}

export function mapUserRows(columns: string[], rows: unknown[][]): UserRow[] {
  const get = makeIndexer(columns);
  return rows.map((r) => {
    const name = str(get(r, "user_name")).trim();
    const email = str(get(r, "user_email")).trim();
    return {
      user_external_id:    str(get(r, "user_external_id")),
      user_name:           name || email || str(get(r, "user_external_id")),
      user_email:          email,
      company_external_id: orNull(get(r, "company_external_id")),
      company_name:        str(get(r, "company_name")),
      calls:               num(get(r, "calls")),
      total_cost:          num(get(r, "total_cost")),
      avg_cost:            num(get(r, "avg_cost")),
      total_tokens:        num(get(r, "total_tokens")),
    };
  });
}

export function mapCompanyDailyRows(columns: string[], rows: unknown[][]): CompanyDailyRow[] {
  const get = makeIndexer(columns);
  return rows.map((r) => ({
    bucket:              str(get(r, "bucket")),
    company_external_id: str(get(r, "company_external_id")),
    company_name:        str(get(r, "company_name")),
    cost:                num(get(r, "cost")),
  }));
}
